// Solid 3D symbols for the column's supports, shared by both 3D backends (the three.js view and
// the SVG isometric "fake 3D" view).
//
// Each support type has one constant Mesh, built once at module load with MeshBuilder. The meshes
// live in a local model space where
//   - the origin is the point on the member the support attaches to,
//   - +x is "up" (the column axis), so the symbol is modelled hanging *below* the origin, i.e.
//     growing towards -x, exactly like the bottom support of a column.
// A support that is not at the base is placed on the side of the member instead by rotating that
// same mesh (see supportRotation below) - which is the whole point of keeping the meshes constant
// and the placement in a transform.
//
// Every shape below is authored in *percent of the symbol's size*: the 100x100x100 box
//   x in [-100, 0], y and z in [-50, 50]
// covers a whole support symbol, so a plate 100 wide is as wide as the symbol gets and a roller of
// radius 12 is 12% of it. Nothing here is in metres. The one conversion to world units happens
// once, in the uniform scale by SUPPORT_ICON_SIZE applied when each mesh is built - so resizing
// every symbol is a single-number change, and the proportions between the parts cannot drift
// while doing it.
import { ng } from "@common/ngds";
import { SupportAxis, SupportConnectivityType } from "@common/helpers/sharedTypes";
import { Vec3 } from "./extrusionEdges";
import { degreesToRadians, Mesh, MeshBuilder, NO_ROTATION, Rotation, scaleMesh } from "./mesh";
import { ClassifiedSupport, getSupports } from "../../helpers/supports";

/**
 * World size of a support symbol - the side of the 100x100x100 percentage box the shapes below are
 * drawn in. In viewport units, i.e. metres, so 1 is a symbol as wide as a one-metre column.
 */
const SUPPORT_ICON_SIZE = 0.4;

/** Percentage space (0-100) to world units. */
const PERCENT_TO_WORLD = SUPPORT_ICON_SIZE / 100;

/** Every dimension below is a percentage of SUPPORT_ICON_SIZE - see the note at the top. */
const style = {
  plateWidth: 100,
  plateThickness: 10,
  capWidth: 65,
  capThickness: 8,
  prismHeight: 50,
  prismBaseWidth: 55,
  prismDepth: 40,
  rollerRadius: 12,
  rollerLength: 50,
  rollerSpacing: 22,
  /**
   * The clear gap left between a cylinder_pair's two "axles" (see buildSideSupportMesh) so they
   * read as two distinct wheel groups rather than fusing into one long roller. Must be positive
   * and, in particular, bigger than the overlap that would otherwise result from spacing them by
   * exactly one diameter - see the doc comment on buildSideSupportMesh.
   */
  rollerPairGap: 15,
  /** A count, not a size - the only number here that is not a percentage. */
  rollerSegments: 14,
};

const buildSideSupportMesh = (body: "none" | "box", feet: "cylinder" | "cylinder_pair"): Mesh => {
  const builder = new MeshBuilder();
  const bodyDepth = body === "box" ? style.capThickness : 0;

  const wheelMultipliers = feet === "cylinder_pair" ? [-1, 1] : [0];
  for (const wheelMulti of wheelMultipliers) {
    builder.addCylinder({
      center: { x: -style.rollerRadius, y: (style.rollerRadius + style.rollerPairGap / 2) * wheelMulti, z: 0 },
      axis: "z",
      radius: style.rollerRadius,
      length: style.rollerLength,
      segments: style.rollerSegments,
    });
  }

  const feetDepth = 2 * style.rollerRadius;

  if (body === "box") {
    builder.addCube({
      center: { x: -feetDepth - bodyDepth / 2, y: 0, z: 0 },
      size: { x: bodyDepth, y: style.capWidth, z: style.capWidth },
      depthHint: "min",
    });
  }

  return scaleMesh(builder.build(), PERCENT_TO_WORLD);
};

/**
 * The three restraint types a base support can be classified as per lateral axis (see
 * classifySupport's isBase branch) - "roller" and "free" never occur there, so the lookup table
 * below only needs to cover these three keys per axis.
 */
type BaseSupportAxisType = Extract<SupportConnectivityType, "rigid" | "pinned" | "rigid_roller">;

const BASE_SUPPORT_AXIS_TYPES: BaseSupportAxisType[] = ["rigid", "pinned", "rigid_roller"];

/** The Z-direction half of the lookup table for one Y-direction type - see BaseSupportMeshes. */
interface BaseSupportZMeshes {
  rigid: Mesh;
  pinned: Mesh;
  rigid_roller: Mesh;
}

/**
 * Every (Y type, Z type) combination a base support can have, each mapped to the one solid that
 * represents it. Keeping this as a plain nested lookup, built once, means a new combination is
 * added by filling in a table cell rather than by teaching a shape-picking function another
 * special case.
 */
interface BaseSupportMeshes {
  rigid: BaseSupportZMeshes;
  pinned: BaseSupportZMeshes;
  rigid_roller: BaseSupportZMeshes;
}

/** Builds the support body, its movable feet where applicable, and the ground plate. */
const buildMesh = (
  // shape of the top part of the mesh
  body: "pyramid" | "prism" | "box",

  // shape of the bottom part of the mesh
  feet: "none" | "cylinder" | "sphere",

  // the flexible axis, body:pyramid and feet:none don't care about direction
  // if other shares get "?", a warning is printed and z is assumed
  axis: "z" | "y" | "?",
): Mesh => {
  const builder = new MeshBuilder();
  const halfWidth = style.prismBaseWidth / 2;
  const supportHeight = style.prismHeight;
  const resolvedAxis = (): "y" | "z" => {
    if (axis === "?") {
      console.warn(`Support mesh "${body}" with "${feet}" feet requires a lateral axis; defaulting to z.`);
      return "z";
    }
    return axis;
  };

  // A body wide enough to span behind both offset feet (cylinder or sphere) risks the same
  // painter's-algorithm depth-smearing as a side support's plate - see Face.depthHint.
  const bodyDepthHint = feet !== "none" ? "min" : undefined;

  switch (body) {
    case "pyramid":
      builder.addPyramid({
        apex: { x: 0, y: 0, z: 0 },
        axis: "x",
        height: -supportHeight,
        profile: [
          [-halfWidth, -halfWidth],
          [halfWidth, -halfWidth],
          [halfWidth, halfWidth],
          [-halfWidth, halfWidth],
        ],
      });
      break;
    case "prism": {
      const flexibleAxis = resolvedAxis();
      builder.addPrism({
        center: { x: 0, y: 0, z: 0 },
        axis: flexibleAxis,
        // The triangular profile has its apex at the member and its base at the foundation.
        // The profile's (u, v) axes depend on the extrusion axis.
        profile:
          flexibleAxis === "z"
            ? [
                [0, 0],
                [-supportHeight, -halfWidth],
                [-supportHeight, halfWidth],
              ]
            : [
                [0, 0],
                [-halfWidth, -supportHeight],
                [halfWidth, -supportHeight],
              ],
        length: style.prismDepth,
        depthHint: bodyDepthHint,
      });
      break;
    }
    case "box":
      builder.addCube({
        center: { x: -supportHeight / 2, y: 0, z: 0 },
        size: { x: supportHeight, y: style.prismBaseWidth, z: style.prismBaseWidth },
        depthHint: bodyDepthHint,
      });
      break;
  }

  let groundTop = -supportHeight;
  if (feet === "cylinder") {
    const flexibleAxis = resolvedAxis();
    const rollerAxis = flexibleAxis === "y" ? "z" : "y";
    for (const offset of [-style.rollerSpacing, style.rollerSpacing]) {
      builder.addCylinder({
        center:
          flexibleAxis === "y"
            ? { x: groundTop - style.rollerRadius, y: offset, z: 0 }
            : { x: groundTop - style.rollerRadius, y: 0, z: offset },
        axis: rollerAxis,
        radius: style.rollerRadius,
        length: style.rollerLength,
        segments: style.rollerSegments,
      });
    }
    groundTop -= 2 * style.rollerRadius;
  } else if (feet === "sphere") {
    for (const y of [-style.rollerSpacing, style.rollerSpacing]) {
      for (const z of [-style.rollerSpacing, style.rollerSpacing]) {
        builder.addSphere({
          center: { x: groundTop - style.rollerRadius, y, z },
          radius: style.rollerRadius,
          segments: style.rollerSegments,
        });
      }
    }
    groundTop -= 2 * style.rollerRadius;
  }

  return scaleMesh(builder.build(), PERCENT_TO_WORLD);
};

/** The constant lookup table itself - every cell built once, at module load, like SUPPORT_MESHES. */
const BASE_SUPPORT_MESHES: BaseSupportMeshes = {
  // fixed in z
  pinned: {
    pinned: buildMesh("pyramid", "none", "?"), // pyramid
    rigid: buildMesh("prism", "none", "y"), // triangle
    rigid_roller: buildMesh("prism", "cylinder", "y"), // triangle with cylinder wheel
  },
  rigid: {
    pinned: buildMesh("prism", "none", "z"), // triangle
    rigid: buildMesh("box", "none", "?"), // "fixed" in both, square
    rigid_roller: buildMesh("box", "cylinder", "z"), // square with cylinder wheel
  },
  rigid_roller: {
    pinned: buildMesh("prism", "cylinder", "z"), // triangle with cylinder wheel
    rigid: buildMesh("box", "cylinder", "y"), // square with cylinder wheel
    rigid_roller: buildMesh("box", "sphere", "?"), // square with whee/spheres
  },
};

const isBaseSupportAxisType = (type: SupportConnectivityType): type is BaseSupportAxisType =>
  (BASE_SUPPORT_AXIS_TYPES as SupportConnectivityType[]).includes(type);

/**
 * Looks up a base support's mesh for its (Y, Z) classification pair, or `null` if that pair has no
 * symbol - a base support is never classified as "roller" (see BaseSupportAxisType), and one side
 * of a support without a lateral definition on that axis classifies as "free", so this is the
 * ordinary way an unrestrained direction ends up drawing nothing.
 */
const getBaseSupportMesh = (yType: SupportConnectivityType, zType: SupportConnectivityType): Mesh | null => {
  if (!isBaseSupportAxisType(yType) || !isBaseSupportAxisType(zType)) return null;
  return BASE_SUPPORT_MESHES[yType][zType];
};

/**
 * One entry per side-support classification, including the explicit non-drawing "free" case.
 * Like BASE_SUPPORT_MESHES, selection is a direct lookup with no branching helper.
 */
interface SideSupportMeshes {
  rigid: Mesh | null;
  pinned: Mesh | null;
  roller: Mesh | null;
  free: Mesh | null;
  rigid_roller: Mesh | null;
}

const SIDE_SUPPORT_MESHES: SideSupportMeshes = {
  free: null,
  rigid: null, // invalid
  pinned: null, // invalid
  roller: buildSideSupportMesh("none", "cylinder"),
  rigid_roller: buildSideSupportMesh("box", "cylinder_pair"),
};

/** A mesh placed in the scene: what to draw, where, and how it is turned. */
export interface MeshInstance {
  mesh: Mesh;
  position: Vec3;
  rotation: Rotation;
  /** What produced this instance - kept for debugging/readability of the render list. */
  kind: SupportConnectivityType;
}

/**
 * Orientation of a support symbol. The mesh is modelled pointing down (-x), so:
 *   - a base support is left alone,
 *   - a support restraining the y axis is rolled +90deg around z, turning "down" into -y,
 *   - a support restraining the z axis is rolled the same way and then yawed +90deg around the
 *     column axis, turning "down" into -z. (Rolling and yawing rather than pitching keeps the
 *     roller cylinders' axes horizontal in both cases.)
 */
const supportRotation = (axis: SupportAxis, isBase: boolean): Rotation => {
  if (isBase) return NO_ROTATION;
  return axis === "Y"
    ? { yaw: 0, pitch: 0, roll: degreesToRadians(90) }
    : { yaw: degreesToRadians(90), pitch: 0, roll: degreesToRadians(90) };
};

interface SupportPlacement {
  /** Half the member's extent along y and z - where a side support attaches. */
  halfY: number;
  halfZ: number;
}

/**
 * The render list: every support in the project turned into a mesh + translation + rotation.
 * Position is in the shared viewport convention (x along the column, positive up; y, z the
 * cross-section axes); each backend maps it into its own world space.
 */
export const collectSupportMeshInstances = (
  project: ng.frontend.column.Project | null,
  { halfY, halfZ }: SupportPlacement,
): MeshInstance[] => {
  if (!project) return [];

  const byAxis: Record<SupportAxis, ClassifiedSupport[]> = {
    Y: getSupports(project, "Y"),
    Z: getSupports(project, "Z"),
  };

  const instances: MeshInstance[] = [];
  const add = (kind: SupportConnectivityType, position: Vec3, rotation: Rotation) => {
    const mesh = SIDE_SUPPORT_MESHES[kind];
    if (!mesh) return;
    instances.push({ mesh, position, rotation, kind });
  };

  const baseSupports = new Set<ClassifiedSupport>();
  for (const support of byAxis.Y) {
    if (!support.isBaseSupport) {
      add(support.type, { x: support.x, y: -halfY, z: 0 }, supportRotation("Y", false));
      continue;
    }
    baseSupports.add(support);
  }

  for (const support of byAxis.Z) {
    if (support.isBaseSupport) {
      baseSupports.add(support);
    } else {
      add(support.type, { x: support.x, y: 0, z: -halfZ }, supportRotation("Z", false));
    }
  }

  for (const support of baseSupports) {
    const ySupport = byAxis.Y.find((candidate) => candidate.support === support.support);
    const zSupport = byAxis.Z.find((candidate) => candidate.support === support.support);
    const yType = ySupport?.type ?? "free";
    const zType = zSupport?.type ?? "free";
    const mesh = getBaseSupportMesh(yType, zType);
    if (mesh) {
      instances.push({
        mesh,
        position: { x: support.x, y: 0, z: 0 },
        rotation: NO_ROTATION,
        kind: yType !== "free" ? yType : zType,
      });
    }
  }

  return instances;
};
